"use client"

// ABOUTME: Subscribe modal for creator pages - email subscription + Drafty follow
// ABOUTME: Similar to Substack's subscribe modal - email-first with optional app follow

import { Fragment, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEnvelope, faUser, faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons"
import Link from "next/link"

interface SubscribeModalProps {
  isOpen: boolean
  onClose: () => void
  publisherId: string
  publisherUsername: string
  publisherDisplayName?: string | null
  publisherBlogTitle?: string | null
  allowEmailSubscriptions?: boolean
  emailOnlySubscribe?: boolean
}

export function SubscribeModal({
  isOpen,
  onClose,
  publisherId,
  publisherUsername,
  publisherDisplayName,
  publisherBlogTitle,
  allowEmailSubscriptions = true,
  emailOnlySubscribe = false,
}: SubscribeModalProps) {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const displayName = publisherBlogTitle || publisherDisplayName || `@${publisherUsername}`

  const handleEmailSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch("/api/email-subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          publisherId: publisherId || undefined,
          publisherUsername,
          email,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to subscribe")
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    // Reset state when closing
    setEmail("")
    setSuccess(false)
    setError(null)
    onClose()
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {success ? (
                  // Success state
                  <div className="text-center py-4">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
                      <FontAwesomeIcon icon={faCheck} className="h-6 w-6 text-green-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Check your inbox
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-600">
                      We sent a confirmation email to <strong>{email}</strong>.
                      Click the link to confirm your subscription.
                    </p>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="mt-6 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  // Subscribe form
                  <>
                    <Dialog.Title as="h3" className="text-xl font-semibold text-gray-900 text-center">
                      Subscribe to {displayName}
                    </Dialog.Title>

                    <p className="mt-2 text-sm text-gray-600 text-center">
                      Get new posts delivered to your inbox.
                    </p>

                    {allowEmailSubscriptions && (
                      <form onSubmit={handleEmailSubscribe} className="mt-6">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="block w-full rounded-lg border border-gray-300 pl-10 pr-4 py-3 text-sm placeholder:text-gray-400 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition"
                            disabled={isLoading}
                          />
                        </div>

                        {error && (
                          <p className="mt-2 text-sm text-red-600">{error}</p>
                        )}

                        <button
                          type="submit"
                          disabled={isLoading}
                          className="mt-4 w-full rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                        >
                          {isLoading ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                              Subscribing...
                            </>
                          ) : (
                            "Subscribe"
                          )}
                        </button>
                      </form>
                    )}

                    {!emailOnlySubscribe && (
                      <>
                        {allowEmailSubscriptions && (
                          <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-gray-200" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                              <span className="bg-white px-4 text-gray-500">or</span>
                            </div>
                          </div>
                        )}

                        <Link
                          href={`/sign-up?follow=${publisherUsername}`}
                          className="flex items-center justify-center gap-2 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                          onClick={handleClose}
                        >
                          <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                          Follow on Drafty
                        </Link>

                        <p className="mt-3 text-xs text-gray-500 text-center">
                          Create a free account to follow and get updates in your feed.
                        </p>
                      </>
                    )}

                    <p className="mt-6 text-xs text-gray-400 text-center">
                      By subscribing, your email address will be shared with{" "}
                      <strong className="text-gray-500">{displayName}</strong>{" "}
                      so they can send you their posts. You can{" "}
                      <span className="text-gray-500">unsubscribe at any time</span> or switch to web-only
                      updates. See Drafty&apos;s{" "}
                      <Link href="/privacy" className="underline hover:text-gray-600">Privacy Policy</Link>.
                    </p>

                    <button
                      type="button"
                      onClick={handleClose}
                      className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700 transition"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
